import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form, InputGroup, Spinner } from "react-bootstrap";
import KanbanBoardContainer from "../utilities/KanbanBoardContainer";
import MyPagination from "../utilities/Pagination";
import { fetchApprovedLoanRequest } from "../../store/actions/adminActions";
import moment from "moment";
import format from "format-number";
import LoanContractModal from "./LoanContractPreviewer";
import LoanDetailModal from "./LoanDetailModal";
import CustomModal from "../utilities/CustomModal";
import GuarantorPreview from "../forms/GuarantorPreview";
import DebitAuthorityModal from "./DebitAuthourityModal";

function LoanSummary() {
  const [currPage, setCurrPage] = useState(1);
  const { loanRequests, isLoading, loanRequestPageNum: numberOfPages } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showGuarantorModal, setShowGurantorModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showDebitAuthority, setShowDebitAuthority] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  useEffect(() => {
    dispatch(fetchApprovedLoanRequest(1));
  }, [dispatch]);

  const flattenObj = (obj) => {
    return Object.keys(obj).reduce((result, key) => {
      if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
        return { ...result, ...flattenObj(obj[key]) };
      }
      return { ...result, [key]: obj[key] };
    }, {});
  };

  const processedLoans = loanRequests.map((loan, index) => ({
    ...flattenObj(loan),
    serial: index + 1,
    deductedInterest: format({ prefix: "₦" })((loan.proRata * loan.amount) / 100),
    proRata: format({ suffix: "%" })(loan.proRata),
    handlingCharge: format({ prefix: "₦" })(loan.amount * 0.005),
    payable: format({ prefix: "₦" })(loan.payable),
    time: moment(loan.requestedAt).format("dddd, MMMM Do YYYY, h:mm:ss a"),
  }));

  const afterPageClicked = (pageNum) => {
    setCurrPage(pageNum);
    dispatch(fetchApprovedLoanRequest(pageNum, searchTerm));
  };

  const onSearch = () => {
    dispatch(fetchApprovedLoanRequest(1, searchTerm));
    setCurrPage(1);
  };

  const onClearSearch = () => {
    setSearchTerm("");
    dispatch(fetchApprovedLoanRequest(1, ""));
    setCurrPage(1);
  };

  const handleRowClick = (loan) => {
    setSelectedLoan(loan);
    setShowModal(true);
  };

  // Sorting logic
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortedLoans = (loans) => {
    if (!sortConfig.key) return loans;

    return [...loans].sort((a, b) => {
      // Handle numeric sorting
      if (!isNaN(a[sortConfig.key]) && !isNaN(b[sortConfig.key])) {
        return sortConfig.direction === 'ascending'
          ? parseFloat(a[sortConfig.key]) - parseFloat(b[sortConfig.key])
          : parseFloat(b[sortConfig.key]) - parseFloat(a[sortConfig.key]);
      }

      // Handle string sorting
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedLoans = getSortedLoans(processedLoans);

  // Column definitions
  const columns = [
    { key: "serial", label: "#", sortable: false },
    { key: "name", label: "Name", sortable: true },
    { key: "memberId", label: "Member Id", sortable: true },
    { key: "amount", label: "Loan Amount(₦)", sortable: true },
    { key: "loanType", label: "Loan Type", sortable: true },
    { key: "duration", label: "Duration (months)", sortable: true },
    { key: "interestRate", label: "Interest rate(%)", sortable: true },
    { key: "proRata", label: "Pro-rated Percentage Interest(%)", sortable: false },
    { key: "deductedInterest", label: "Interest Deducted at source", sortable: false },
    { key: "handlingCharge", label: "Handling charge deducted at source", sortable: false },
    { key: "payable", label: "Amount Payable Per month", sortable: true },
  ];

  // Export to CSV function
  const exportToCSV = () => {
    const headers = columns.map(col => col.label).join(',');
    const rows = processedLoans.map(loan => {
      return columns.map(col => {
        // Handle commas and quotes in CSV fields
        const cellValue = loan[col.key] || '';
        const formattedValue = typeof cellValue === 'string' && cellValue.includes(',')
          ? `"${cellValue}"`
          : cellValue;
        return formattedValue;
      }).join(',');
    }).join('\n');

    const csvContent = `${headers}\n${rows}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'approved_loans.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden"></span>
        </Spinner>
      </div>
    );
  }

  return (
    <KanbanBoardContainer>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Loan Requests</h3>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-end mb-2">
            <Button variant="primary" onClick={exportToCSV}>
              Export To CSV
            </Button>
          </div>
          <div className="d-flex mb-3">
            <InputGroup>
              <Form.Control
                style={{ flexGrow: 1 }}
                type="text"
                placeholder="Search loans by LoanID or MemberId..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && onSearch()}
              />
              <Button variant="outline-success" onClick={onSearch}>
                Search
              </Button>
              <Button variant="danger" onClick={onClearSearch}>
                Clear
              </Button>
            </InputGroup>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      style={{
                        backgroundColor: "#c8e6c9",
                        width: "200px",
                        cursor: column.sortable ? 'pointer' : 'default'
                      }}
                      onClick={() => column.sortable ? requestSort(column.key) : null}
                    >
                      {column.label}
                      {column.sortable && (
                        <span className="ml-1">
                          {sortConfig.key === column.key ? (
                            sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'
                          ) : ''}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedLoans.map((loan) => (
                  <tr
                    key={loan._id}
                    onClick={() => handleRowClick(loan)}
                    style={{ cursor: 'pointer' }}
                  >
                    {columns.map((column) => (
                      <td key={`${loan._id}-${column.key}`}>
                        {loan[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
                {sortedLoans.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="text-center">
                      No loan requests found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <MyPagination
            currentPage={currPage}
            numberOfPages={numberOfPages}
            pageClicked={afterPageClicked}
          />
        </div>
      </div>

      <LoanDetailModal
        show={showModal}
        onHide={() => setShowModal(false)}
        loan={selectedLoan}
        setShowContractModal={setShowContractModal}
        showGuarantor={setShowGurantorModal}
        setShowAuthority={setShowDebitAuthority}
      />
      <CustomModal hide={() => setShowGurantorModal(false)} show={showGuarantorModal} size='xl'>
        <GuarantorPreview data={selectedLoan} />
      </CustomModal>
      <LoanContractModal
        show={showContractModal}
        onHide={() => setShowContractModal(false)}
        loanDetails={selectedLoan}
      />
      <DebitAuthorityModal
        show={showDebitAuthority}
        onHide={() => setShowDebitAuthority(false)}
        loanDetails={selectedLoan}
      />
    </KanbanBoardContainer>
  );
}

export default LoanSummary;